import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { ReportsService } from "../../services";
import { currency } from "../../utils/format";

const STOCK_VIEWS = {
  BOTTLE: "BOTTLE",
  RESTAURANT: "RESTAURANT",
};

const getStockTypeLabel = (row) =>
  row.stockItemType === "RESTAURANT" ? "RESTAURANT" : "BOTTLE";

const getStockProductName = (row) =>
  row.productName || row.bottleName || row.biteName || "—";

const getStockCategoryName = (row) =>
  row.categoryName ||
  row.companyName ||
  row.bottleTypeName ||
  row.biteCategoryName ||
  "—";

const getStockUnitLabel = (row) => {
  if (row.unitLabel) {
    return row.unitLabel;
  }

  if (row.volumeMl) {
    return `${row.volumeMl}ML`;
  }

  if (row.bottleSize) {
    return row.bottleSize;
  }

  return "Units";
};

const getStockRowKey = (row, index) =>
  `${getStockTypeLabel(row)}-${
    row.productId || row.bottleId || row.biteProductId || index
  }`;

const isLowStockRow = (row) =>
  Number(row.availableQuantity || 0) <= Number(row.lowStockLevel || 0);

export const LowStockPage = () => {
  const [rows, setRows] = useState([]);
  const [stockView, setStockView] = useState(STOCK_VIEWS.BOTTLE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ReportsService.stockSummary()
      .then((data) => {
        const lowStockRows = (data || []).filter(isLowStockRow);
        setRows(lowStockRows);
      })
      .finally(() => setLoading(false));
  }, []);

  const bottleRows = useMemo(
    () => rows.filter((row) => getStockTypeLabel(row) === STOCK_VIEWS.BOTTLE),
    [rows]
  );

  const restaurantRows = useMemo(
    () =>
      rows.filter(
        (row) => getStockTypeLabel(row) === STOCK_VIEWS.RESTAURANT
      ),
    [rows]
  );

  const visibleRows =
    stockView === STOCK_VIEWS.RESTAURANT ? restaurantRows : bottleRows;

  const stockViewLabel =
    stockView === STOCK_VIEWS.RESTAURANT ? "Restaurant" : "BAR Product";

  const columns =
    stockView === STOCK_VIEWS.RESTAURANT
      ? [
          {
            key: "itemSection",
            header: "Section",
            render: (r) => r.itemSection || "FOOD",
          },
          {
            key: "categoryName",
            header: "Category",
            render: (r) => getStockCategoryName(r),
          },
          {
            key: "productName",
            header: "Item",
            render: (r) => (
              <span className="font-medium text-slate-900">
                {getStockProductName(r)}
              </span>
            ),
          },
          {
            key: "unitLabel",
            header: "Unit",
            render: (r) => getStockUnitLabel(r),
          },
          {
            key: "availableQuantity",
            header: "Available",
            render: (r) => (
              <span className="text-rose-600 font-medium">
                {r.availableQuantity}
              </span>
            ),
          },
          {
            key: "lowStockLevel",
            header: "Threshold",
          },
          {
            key: "sellingPrice",
            header: "Selling",
            render: (r) => currency(r.sellingPrice),
          },
          {
            key: "status",
            header: "",
            render: () => <StatusBadge value="LOW_STOCK" />,
          },
        ]
      : [
          {
            key: "companyName",
            header: "Company",
            render: (r) => r.companyName || getStockCategoryName(r),
          },
          {
            key: "bottleName",
            header: "Product",
            render: (r) => (
              <span className="font-medium text-slate-900">
                {r.bottleName || getStockProductName(r)}
              </span>
            ),
          },
          {
            key: "volumeMl",
            header: "Volume / Unit",
            render: (r) =>
              r.volumeMl ? `${r.volumeMl}ML` : getStockUnitLabel(r),
          },
          {
            key: "bottleSize",
            header: "Size",
            render: (r) => r.bottleSize || "—",
          },
          {
            key: "availableQuantity",
            header: "Available",
            render: (r) => (
              <span className="text-rose-600 font-medium">
                {r.availableQuantity}
              </span>
            ),
          },
          {
            key: "lowStockLevel",
            header: "Threshold",
          },
          {
            key: "sellingPrice",
            header: "Selling",
            render: (r) => currency(r.sellingPrice),
          },
          {
            key: "status",
            header: "",
            render: () => <StatusBadge value="LOW_STOCK" />,
          },
        ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Low Stock"
        subtitle="BAR and restaurant products at or below their configured low-stock threshold."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Total Low Stock
          </div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-1">
            {loading ? "…" : rows.length}
          </div>
        </div>

        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-blue-700">
            BAR Product Low Stock
          </div>
          <div className="font-display text-2xl font-bold text-blue-800 mt-1">
            {loading ? "…" : bottleRows.length}
          </div>
        </div>

        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">
            Restaurant Low Stock
          </div>
          <div className="font-display text-2xl font-bold text-amber-800 mt-1">
            {loading ? "…" : restaurantRows.length}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setStockView(STOCK_VIEWS.BOTTLE)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              stockView === STOCK_VIEWS.BOTTLE
                ? "bg-white text-navy-800 shadow-sm"
                : "text-slate-600"
            }`}
          >
            BAR Product Stock
          </button>

          <button
            type="button"
            onClick={() => setStockView(STOCK_VIEWS.RESTAURANT)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              stockView === STOCK_VIEWS.RESTAURANT
                ? "bg-white text-navy-800 shadow-sm"
                : "text-slate-600"
            }`}
          >
            Restaurant Stock
          </button>
        </div>

        <div className="text-xs text-slate-500">
          Showing {stockViewLabel.toLowerCase()} low-stock items
        </div>
      </div>

      <DataTable
        loading={loading}
        rows={visibleRows.map((r, index) => ({
          ...r,
          key: getStockRowKey(r, index),
        }))}
        empty={`No low-stock ${stockViewLabel.toLowerCase()} items. Inventory looks healthy.`}
        testId="low-stock-table"
        columns={columns}
      />
    </div>
  );
};
